import React, { useEffect, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import client from "@/lib/apiClient";
import { useLang } from "@/i18n";
import { useProperty } from "@/context/PropertyContext";
import { SectionHeader, Loader, Lu } from "@/components/shared";

export default function Occupancy() {
  const { t } = useLang();
  const { selectedId } = useProperty();
  const [series, setSeries] = useState(null);
  const [floors, setFloors] = useState([]);

  useEffect(() => {
    if (!selectedId) return;
    setSeries(null);
    Promise.all([
      client.get(`/properties/${selectedId}/occupancy`),
      client.get(`/floors/${selectedId}`),
    ]).then(([o, f]) => { setSeries(o.data.series || []); setFloors(f.data.floors || []); });
  }, [selectedId]);

  if (!series) return <Loader />;

  const heatColor = (r) => {
    if (r.status === "alert") return "#F43F5E";
    if (r.status === "maintenance") return "#F59E0B";
    if (r.status === "occupied") return r.temp >= 25 ? "#EF9A44" : "#10B981";
    return "#334155";
  };

  return (
    <div>
      <SectionHeader icon="users-round" title={t("occupancy")} subtitle={t("occIntro")} />
      <div className="md-card p-5 mb-5">
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={series}>
            <defs>
              <linearGradient id="oSensor" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1E5EFF" stopOpacity={0.4} /><stop offset="100%" stopColor="#1E5EFF" stopOpacity={0} /></linearGradient>
              <linearGradient id="oPms" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} /><stop offset="100%" stopColor="#3B82F6" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--md-soft))" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fontSize: 10, fill: "#64748B" }} domain={[0, 100]} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, color: "hsl(var(--foreground))" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="sensor_occupancy" stroke="#1E5EFF" fill="url(#oSensor)" strokeWidth={2} name={t("sensorOcc")} />
            <Area type="monotone" dataKey="pms_occupancy" stroke="#3B82F6" fill="url(#oPms)" strokeWidth={2} name={t("pmsOcc")} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="md-card p-5">
        <h3 className="font-semibold text-foreground mb-1 flex items-center gap-2"><Lu name="thermometer-sun" className="w-4 h-4 text-primary" />{t("floor")} Heatmap</h3>
        <p className="text-xs text-muted-foreground/70 mb-4">{t("liveData")}</p>
        <div className="space-y-3">
          {floors.map((f) => (
            <div key={f.floor} className="flex items-center gap-3">
              <span className="w-7 h-7 rounded-md bg-foreground/5 flex items-center justify-center text-xs font-mono text-primary shrink-0">{f.floor}</span>
              <div className="flex flex-wrap gap-1.5">
                {f.rooms.map((r) => (
                  <div key={r.id} title={`${r.number} · ${r.temp}°`} data-testid={`heat-${r.number}`}
                    className="w-8 h-8 rounded-md flex items-center justify-center text-[9px] font-mono text-white/90"
                    style={{ background: heatColor(r), opacity: 0.85 }}>
                    {r.temp}°
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 mt-5 text-xs text-muted-foreground">
          {[["#10B981", t("occupied")], ["#EF9A44", "Hot"], ["#334155", t("vacant")], ["#F59E0B", t("maintenance")], ["#F43F5E", t("alert")]].map(([c, l]) => (
            <span key={l} className="flex items-center gap-1.5"><span className="w-3 h-3 rounded" style={{ background: c }} />{l}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
